export const JOB_TYPES = {
  save_grades: "Notas",
  save_frequency: "Frequência",
  save_occurrences: "Ocorrências",
  generate_class_report: "Relatório",
  sync_lb: "Sincronização de dados externos",
  process_smart_minutes: "Ata Inteligente",
  save_acl_profile: "Perfil de acesso",
  save_class_plan: "Plano de aula",
  save_class_stage: "Etapas"
};

export const getJobTypeLabel = name => JOB_TYPES[name] || name;

const INDIVIDUAL_PERIOD = "Individual";

export const isIndividualOccurrenceJob = (job, classId, studentId) =>
  !!job &&
  job.name === "save_occurrences" &&
  (classId == null || job.classId === classId) &&
  job.period === INDIVIDUAL_PERIOD &&
  (studentId == null || String(job.course) === String(studentId));

export const JOB_IN_PROGRESS_STATUSES = ["pending", "retrying", "paused"];

export const isJobInProgress = jobOrStatus => {
  const status =
    typeof jobOrStatus === "string"
      ? jobOrStatus
      : jobOrStatus && jobOrStatus.status;
  return JOB_IN_PROGRESS_STATUSES.indexOf(status) !== -1;
};

/**
 * Mesma regra do FAB / worker:
 * pending/retrying → pending
 * paused → paused
 * error → error (falha total)
 * success + error parcial → warning
 * success → success
 */
export const getJobDisplayStatus = job => {
  if (!job) return "pending";
  if (job.status === "paused") return "paused";
  if (isJobInProgress(job)) return "pending";
  if (job.status === "error") return "error";
  if (job.status === "success" && job.error) return "warning";
  if (job.status === "success") return "success";
  return job.status;
};

export const getJobStatusLabel = displayStatus => {
  switch (displayStatus) {
    case "pending":
      return "Em andamento";
    case "paused":
      return "Pausado";
    case "success":
      return "Concluída";
    case "warning":
      return "Concluída com erros";
    case "error":
      return "Erro";
    default:
      return displayStatus;
  }
};

export const getJobStatusIcon = displayStatus => {
  switch (displayStatus) {
    case "pending":
      return "hourglass_empty";
    case "paused":
      return "pause_circle_outline";
    case "success":
      return "check_circle_outline";
    case "warning":
      return "report_problem";
    case "error":
      return "highlight_off";
    default:
      return "history";
  }
};

export const getJobStatusColor = displayStatus => {
  switch (displayStatus) {
    case "pending":
      return "#1C1F74";
    case "paused":
      return "#E0A100";
    case "success":
      return "#5BC259";
    case "warning":
      return "#ffcc00";
    case "error":
      return "#EE4848";
    default:
      return "#828282";
  }
};

export const getJobAttemptsMessage = job => {
  const attempts = Number(job && job.attempts) || 0;
  if (attempts <= 0) return "";
  if (attempts === 1) {
    return "Fizemos 1 tentativa para enviar os dados.";
  }
  return `Fizemos ${attempts} tentativas para enviar os dados.`;
};

export const canResendJob = job => {
  if (!job) return false;
  return job.status === "error" && !job.resentAt;
};

const getPendingMessage = job => {
  if (job.name === "save_frequency") {
    return "Salvando frequência. Por favor, aguarde.";
  }
  if (job.name === "save_occurrences") {
    return "Salvando ocorrências. Por favor, aguarde.";
  }
  if (job.name === "generate_class_report") {
    return "Gerando relatório. Por favor, aguarde.";
  }
  if (job.name === "sync_lb") {
    return "Sincronizando dados externos. Por favor, aguarde.";
  }
  if (job.name === "process_smart_minutes") {
    return "Processando áudio da ata. Por favor, aguarde.";
  }
  if (job.name === "save_acl_profile") {
    return "Instalando perfil no sistema escolar. Por favor, aguarde.";
  }
  if (job.name === "save_class_stage") {
    return "Salvando etapa. Por favor, aguarde.";
  }
  return "Carregando notas. Por favor, aguarde.";
};

const getSuccessMessage = job => {
  if (job.name === "save_frequency") {
    return "Frequência salva com sucesso!";
  }
  if (job.name === "save_occurrences") {
    return "Ocorrências salvas com sucesso!";
  }
  if (job.name === "generate_class_report") {
    return "Relatório pronto para baixar!";
  }
  if (job.name === "sync_lb") {
    return "Sincronização de dados externos concluída!";
  }
  if (job.name === "process_smart_minutes") {
    return "Ata gerada com sucesso!";
  }
  if (job.name === "save_acl_profile") {
    return "Perfil instalado no sistema escolar!";
  }
  if (job.name === "save_class_stage") {
    return "Etapa salva com sucesso!";
  }
  return "Notas salvas com sucesso!";
};

/**
 * Título da ata para exibição (nome do atendimento), sem prefixo "Turma…".
 */
export const getSmartMinutesDisplayTitle = job => {
  if (!job || job.name !== "process_smart_minutes") return null;
  const payload = job.payload || {};
  const response = job.response || {};
  const fromFields = job.ataTitle || payload.title || response.title || null;
  if (fromFields && String(fromFields).trim()) {
    return String(fromFields).trim();
  }
  const full = String(job.title || "").trim();
  if (!full) return "Ata Inteligente";
  const pipe = full.match(/\|\s*(.+)$/);
  return (pipe && pipe[1].trim()) || full;
};

/** Data/hora da reunião (ou criação do job) para o link da ata. */
export const getSmartMinutesWhenLabel = job => {
  if (!job || job.name !== "process_smart_minutes") return null;
  const payload = job.payload || {};
  const response = job.response || {};
  const raw =
    job.meetingAt ||
    payload.meetingAt ||
    response.meetingAt ||
    job.createdAt ||
    null;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const pad = n => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/** Rota do detalhe da ata a partir do job process_smart_minutes. */
export const getSmartMinutesJobLink = job => {
  if (!job || job.name !== "process_smart_minutes") return null;
  const response = job.response || {};
  const payload = job.payload || {};
  const draftId = job.draftId || response.draftId || payload.draftId || null;
  if (!draftId) return null;
  const classId = job.classId || payload.classId || response.classId || null;
  if (classId) {
    return `/classes/${classId}/ata-inteligente/${draftId}`;
  }
  const students = payload.students || response.students || [];
  const first = students[0] || null;
  const studentId = first && (first.ra || first.id);
  if (studentId) {
    return `/student/${studentId}/ata-inteligente/${draftId}`;
  }
  return null;
};

const getErrorMessage = job => {
  if (job.name === "save_frequency") {
    return "Erro ao salvar frequência.";
  }
  if (job.name === "save_occurrences") {
    return "Erro ao salvar ocorrências.";
  }
  if (job.name === "generate_class_report") {
    return "Erro ao gerar relatório.";
  }
  if (job.name === "sync_lb") {
    return "Erro na sincronização de dados externos.";
  }
  if (job.name === "process_smart_minutes") {
    return "Erro ao processar a ata.";
  }
  if (job.name === "save_acl_profile") {
    return "Erro ao instalar o perfil no sistema escolar.";
  }
  if (job.name === "save_class_stage") {
    return "Erro ao salvar etapa.";
  }
  return "Erro ao salvar notas.";
};

export const getJobStatusMessage = job => {
  if (isJobInProgress(job)) {
    if (job.status === "paused") {
      return "Sistema acadêmico indisponível. O envio será retomado automaticamente.";
    }
    if (job.status === "retrying") {
      return "Reenviando. Por favor, aguarde.";
    }
    return getPendingMessage(job);
  }
  if (job.status === "success") {
    if (job.error) {
      return "Solicitação finalizada com alguns erros";
    }
    return getSuccessMessage(job);
  }
  if (job.status === "error") {
    return getErrorMessage(job);
  }
  return "";
};
